import React, { useEffect, useRef, useState } from 'react';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import VolumeOffRoundedIcon from '@mui/icons-material/VolumeOffRounded';
import Tooltip from '@src/components/Tooltip';
import VolumePopover from '@src/new-components/Timeline/VolumePopover';
import { StyledGutterButton, StyledGutterCell } from './index.style';

// Grace period bridging the small gap between the gutter button and the
// popover so moving the pointer across it doesn't flicker the popover closed.
const CLOSE_DELAY_MS = 120;

// Pull the tooltip back against the icon. MUI leaves a 14px margin between the
// tooltip and its anchor, of which the arrow only fills ~10px, and the anchor
// here is the 32px button — 6px of padding wider than the 20px icon inside it.
// The label ends up floating ~10px clear of the glyph it names; -6 closes that
// to the arrow sitting on the button's edge.
const TOOLTIP_POPPER_PROPS = {
  popperOptions: {
    modifiers: [{ name: 'offset', options: { offset: [0, -10] } }],
  },
};

/**
 * Left-gutter cell for one timeline track: a type icon + a volume control.
 * Presentational and reused by every track (voice / media / music). `height`
 * overrides the row height so a taller track (e.g. media) stays aligned.
 * `label` names the track — it is the icon's tooltip and both buttons'
 * aria-label.
 *
 * When `onVolumeChange` is supplied, hovering the volume button opens a level
 * popover (slider + %); clicking it still toggles mute (unchanged behaviour).
 */
const TrackGutter = ({
  icon,
  label,
  height,
  muted = false,
  onToggleMute,
  volume = 0,
  onVolumeChange,
  onVolumeCommit,
  // A download/export mixes the current levels into the file it is building, so
  // the control is frozen while one is in flight.
  volumeDisabled = false,
}) => {
  const hasVolumeControl =
    typeof onVolumeChange === 'function' && !volumeDisabled;
  const [anchorEl, setAnchorEl] = useState(null);
  const closeTimer = useRef(null);

  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  };
  const openPopover = (el) => {
    cancelClose();
    setAnchorEl(el);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setAnchorEl(null), CLOSE_DELAY_MS);
  };
  useEffect(
    () => () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    },
    [],
  );

  return (
    <StyledGutterCell style={height ? { height: `${height}px` } : undefined}>
      {/* The icon alone doesn't say which track this row is — the tooltip
          names it. Same text as the aria-label, so both agree. */}
      <Tooltip
        arrow
        title={label}
        placement="top"
        PopperProps={TOOLTIP_POPPER_PROPS}
      >
        <StyledGutterButton
          type="button"
          aria-label={label}
          tabIndex={-1}
          $interactive={false}
        >
          {icon}
        </StyledGutterButton>
      </Tooltip>
      <StyledGutterButton
        type="button"
        aria-label={`${label} volume`}
        onClick={onToggleMute}
        disabled={volumeDisabled}
        onMouseEnter={
          hasVolumeControl
            ? (event) => openPopover(event.currentTarget)
            : undefined
        }
        onMouseLeave={hasVolumeControl ? scheduleClose : undefined}
      >
        {muted ? <VolumeOffRoundedIcon /> : <VolumeUpRoundedIcon />}
      </StyledGutterButton>
      {hasVolumeControl && (
        <VolumePopover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          volume={volume}
          muted={muted}
          onClose={() => setAnchorEl(null)}
          onVolumeChange={onVolumeChange}
          onVolumeCommit={onVolumeCommit}
          onToggleMute={onToggleMute}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      )}
    </StyledGutterCell>
  );
};

export default TrackGutter;
